import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FirstRun } from '../lib/firstRun'
import type { Layout } from '../lib/layout'
import { currentStep, stepDone, stepsFor, type TourSnapshot } from '../lib/tour'

// The first run's tour (lib/tour.ts) over the app's own state: the step to
// show, a do step moved on once it happened since it began, a look step by
// Got it (`next`), the end after the last one or on Skip tour. Progress is
// saved with the first run, so a reload or another phone picks up there.

export function useTour({ firstRun, save, layout, getStarted, snapshot }: {
  firstRun: FirstRun | null
  save: (patch: Partial<FirstRun>) => void
  layout: Layout
  getStarted: boolean
  snapshot: TourSnapshot
}) {
  const touring = firstRun?.stage === 'tour'
  const { shopsWith, joined, tourSeen } = firstRun ?? {}
  const steps = useMemo(() => stepsFor({ layout, shopsWith, joined, getStarted }), [layout, shopsWith, joined, getStarted])
  const seen = useMemo(() => tourSeen ?? [], [tourSeen])
  const step = touring ? currentStep(steps, seen) : null

  // What the app looked like when this step began.
  const [begun, setBegun] = useState<{ id: string; at: TourSnapshot } | null>(null)
  if (step && begun?.id !== step.id) setBegun({ id: step.id, at: snapshot })

  // Passed once: a re-render before the save comes back mustn't pass it again.
  const passed = useRef<string | null>(null)
  const pass = useCallback((id: string) => {
    if (passed.current === id) return
    passed.current = id
    const nextSeen = [...seen, id]
    const left = steps.filter(s => !nextSeen.includes(s.id))
    save(left.length ? { tourSeen: nextSeen } : { tourSeen: nextSeen, stage: 'done' })
  }, [seen, steps, save])

  const done = !!step && step.kind === 'do' && begun?.id === step.id && stepDone(step, snapshot, begun.at)
  useEffect(() => { if (done && step) pass(step.id) }, [done, step, pass])

  const next = useCallback(() => { if (step) pass(step.id) }, [step, pass])
  const skip = useCallback(() => save({ stage: 'done' }), [save])

  return {
    step,
    number: step ? steps.indexOf(step) + 1 : 0,
    total: steps.length,
    next,
    skip,
  }
}
